import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import InfoCard from "../../../components/components/js/InfoCard";
import { Masonry } from "masonic";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import AuthContext from "../../../context/AuthProvider";
import RoomsCard from "../../../components/components/js/RoomsCard";
import ContactsTable from "../../../components/components/js/ContactsCard";
import ActivityFeed from "../../../components/components/js/FeedComponent";
import { Avatar, Box, Typography, Button, IconButton } from "@mui/material";
import { DeleteConfirmationDialog } from "../../../components/components/js/DeleteConfirmationDialog";
import { formatAge } from "../../../utils/exportUtils";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";

export const StudentDetails = () => {
  const { studentId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { school, auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const [cardData, setCardData] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({ name: "", image: "" });
  const [activeTab, setActiveTab] = useState("feed");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef();

  const fetchAllData = useCallback(async () => {
    try {
      setNotFound(false);

      const [
        studentRes,
        roomsRes,
        contactsRes,
        gendersRes,
        nationalitiesRes,
        countriesRes,
        parentsRes,
      ] = await Promise.all([
        axiosPrivate.get(API_URLS.getStudent(studentId, school.id)),
        axiosPrivate.get(API_URLS.getRooms(auth.id, school.id)),
        axiosPrivate.get(API_URLS.getStudentContacts(studentId , school.id)),
        axiosPrivate.get(API_URLS.gender),
        axiosPrivate.get(API_URLS.nationality),
        axiosPrivate.get(API_URLS.country),
        axiosPrivate.get(`${API_URLS.getParents(auth.id, school.id)}`),
      ]);
      const studentData = studentRes.data;
      const roomsData = studentRes.data.rooms;
      const rooms = roomsRes.data;
      const genders = gendersRes.data.map((g) => ({
        id: g.value,
        name: g.label,
      }));
      const nationalities = nationalitiesRes.data.map((n) => ({
        id: n.id,
        name: n.name,
        code: n.code,
      }));
      const countries = countriesRes.data.map((c) => ({
        id: c.id,
        name: c.name,
        code: c.code
      }));
      const parents = parentsRes.data.results.map((p) => ({
        id: p.id,
        name: `${p.first_name} ${p.last_name ?? ""}`.trim(),
        email: p.email,
      }));

      // const currentRooms = roomsData
      //   ? roomsData.map((room) => ({
      //       id: room.id,
      //       room: room.name,
      //     }))
      //   : [];
      const allRooms = rooms.results.map((room) => ({
        id: room.id,
        room: room.name,
      }));
      const currentRooms = studentRes.data.rooms_names
        ? studentRes.data.rooms_names.map((c) => ({ id: c.id, room: c.name }))
        : [];
      setPersonalInfo({
        name: `${studentData.first_name} ${studentData.last_name || ""}`.trim(),
        image: studentData.profile_img,
      });

      const personal = [
        { 
          label: "First Name", 
          value: studentData.first_name, 
          editable: true 
        },
        { 
          label: "Last Name", 
          value: studentData.last_name || "", 
          editable: true 
        },
        {
          label: "BirthDate",
          value: studentData.birth_date,
          type: "date",
          editable: true,
        },
        {
          label: "Age",
          value: formatAge(studentData.birth_date , i18n.language),
          editable: false,
        },
        {
          label: "Gender",
          value: studentData.gender,
          type: "select",
          options: genders,
          editable: true,
        },
        {
          label: "Country",
          value: studentData.country,
          type: "select",
          options: countries,
          editable: true,
        },
        {
          label: "Nationality",
          value: studentData.nationality,
          type: "select",
          options: nationalities,
          editable: true,
        },
        { label: "Allergies", value: studentData.allergies, editable: true },
        { label: "Notes", value: studentData.notes, editable: true },
        {
          label: "Medications",
          value: studentData.medications,
          editable: true,
        },
        { label: "Doctor Name", value: studentData.doctor_name, editable: true },
        {
          label: "Doctor Phone No.",
          value: studentData.doctor_phone,
          editable: true,
        },
      ];

      const schoolData = [
        {
          label: "Status",
          value: studentData.status,
          type: "select",
          options: [
            { id: "active", name: "Active" },
            { id: "inactive", name: "Inactive" },
          ],
          editable: true,
        },
        { label: "Student ID", value: studentData.student_id, editable: true },
      ];

      const address = [
        { label: "Street", value: studentData.street, editable: true },
        {
          label: "Appartment",
          value: studentData.apartment_house,
          editable: true,
        },
        { label: "City", value: studentData.city, editable: true },
        { label: "State", value: studentData.state, editable: true },
        { label: "Zip Code", value: studentData.zip_code, editable: true },
      ];

      const parents_data = [
        {
          label: "Parents",
          value: studentData.parents,
          type: "multi-select",
          options: parents,
          editable: true,
          modalData: [
            {
              key: "gender",
              label: "Gender",
              options: genders,
            },
            { key: "countries", label: "Country", options: countries },
          ],
        },
      ];

      const finalData = [
        { type: "info", title: "Personal Info", data: personal },
        { type: "rooms", initialSelected: currentRooms},
        { type: "info", title: "School Details", data: schoolData },
        { type: "info", title: "Address", data: address, fullWidth: true },
        { type: "info", title: "Parents", data: parents_data, fullWidth: true },
      ];

      const contactsData = contactsRes.data.map((contact) => ({
        id: contact.id,
        initials:
          (contact?.first_name?.[0] || "") + (contact?.last_name?.[0] || ""),
        name: `${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`,
        email: contact.email ?? "-",
        phone: contact.phone ?? "-",
        image: contact.profile_img,
        canPickUp: contact.can_pick_up ?? false,
        signedUp: contact.signed_up ?? false,
        contactType: contact.contact_type ?? "-",
        contactStatus: contact.contact_status ?? "-",
      }));

      setCardData(finalData);
      setContacts(contactsData);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error("Error fetching data", err);
      }
    }
  }, [studentId, auth.id, axiosPrivate, school.id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleDelete = async () => {
    try {
      await axiosPrivate.delete(API_URLS.deleteStudent(studentId, school.id));
      navigate(-1);
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const updateImage = async (img) => {
    try {
      const formData = new FormData();
      formData.append("profile_img", img);
      const response = await axiosPrivate.patch(
        API_URLS.getStudent(studentId, school.id),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("Patch success", response);
      navigate(0);
    } catch (err) {
      console.error("Patch error", err);
    }
  };

  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleRoomsSave = async (updates) => {
    try {
      await axiosPrivate.patch(
        API_URLS.getStudent(studentId, school.id),
        updates,
        { headers: { "Content-Type": "application/json" } }
      );
      await fetchAllData();
    } catch (error) {
      console.error("Error updating rooms:", error);
      throw error;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      updateImage(file);
    }
  };

  const fullWidthCards = cardData.filter((card) => card.fullWidth);
  const masonryCards = cardData.filter((card) => !card.fullWidth);

  if (notFound) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
        textAlign="center"
      >
        <Typography variant="h4" color="primary" gutterBottom>
          {t(`studentDetails.notFound.title`)}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t(`studentDetails.notFound.description`)}
        </Typography>
      </Box>
    );
  }

  return (
    <div className={`p-5 d-block ms-auto px-4`} style={{ minHeight: "70vh" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        {["feed", "profile"].map((tab) => (
          <Typography
            key={tab}
            onClick={() => setActiveTab(tab)}
            fontSize={18}
            sx={{
              borderBottom: activeTab === tab ? "2px solid purple" : "none",
              fontWeight: activeTab === tab ? "bold" : "normal",
              cursor: "pointer",
            }}
          >
            {t(`studentDetails.tabs.${tab}`)}
          </Typography>
        ))}
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: "5%", pr: "35px" }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box position="relative" display="inline-block">
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: -4,
                left: -4,
                width: 93,
                height: 93,
                borderRadius: "50%",
                border: `1px solid ${theme.palette.primary.main}`,
                pointerEvents: "none",
              })}
            />
            {personalInfo.image ? (
              <Avatar
                src={personalInfo.image}
                alt="img"
                sx={{ width: 85, height: 85 }}
              />
            ) : (
              <FaUserCircle size={85} />
            )}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <IconButton
              size="small"
              sx={(theme) => ({
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 25,
                height: 25,
                backgroundColor: "white",
                borderRadius: "50%",
                boxShadow: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                "&:hover": { backgroundColor: "#e3f2fd" },
              })}
              aria-label="edit"
            >
              <EditIcon
                fontSize="small"
                color="primary"
                onClick={handleEditClick}
              />
            </IconButton>
          </Box>

          <Typography variant="h5" fontWeight="bold">
            {personalInfo?.name}
          </Typography>
        </Box>
      </Box>

      {activeTab === "feed" ? (
        <ActivityFeed studentId={studentId} />
      ) : (
        <div style={{ padding: "35px" }}>
          <Masonry
            items={masonryCards}
            columnGutter={18}
            columnWidth={400}
            render={({ data }) => (
              <div>
                {data.type === "info" ? (
                  <InfoCard
                    title={data.title}
                    editable
                    data={data.data}
                    id={studentId}
                    type={"student"}
                    onSaveSuccess={fetchAllData}
                  />
                ) : (
                  <RoomsCard
                    roomsApiUrl={API_URLS.getRooms(auth.id, school.id)}
                    axiosPrivate={axiosPrivate}
                    initialSelected={data.initialSelected}
                    id={studentId}
                    type={"student"}
                    onSave={handleRoomsSave}
                    editable={true}
                  />
                )}
              </div>
            )}
          />
          {fullWidthCards.map((data, idx) => (
            <div key={`full-${idx}`}>
              {data.type === "rooms" ? (
                <RoomsCard
                  roomsApiUrl={API_URLS.getRooms(auth.id, school.id)}
                  axiosPrivate={axiosPrivate}
                  initialSelected={data.initialSelected}
                  id={studentId}
                  type={"student"}
                  onSave={handleRoomsSave}
                  editable={true}
                />
              ) : (
                <InfoCard
                  title={data.title}
                  editable
                  data={data.data}
                  id={studentId}
                  type={"student"}
                  onSaveSuccess={fetchAllData}
                />
              )}
            </div>
          ))}
          <ContactsTable contacts={contacts} studentId={studentId} />


          <Box  className="info-card mt-3">
            <div className="info-card-header mb-0">
              <Typography variant="header">
                {isAr? 'منطقه خطر' : 'Danger Zone'}
              </Typography>
            </div>
            
            <Button
              variant="text"
              onClick={() => setShowDeleteConfirm(true)}
              sx={{
                margin: "1rem",
                color: "red",
                fontWeight: "bold",
                fontSize: "1.1rem",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {t(`studentDetails.deleteButton`)}
            </Button>
          </Box>
        </div>
      )}

      {showDeleteConfirm && (
        <DeleteConfirmationDialog
          onDelete={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};